/**
 * @module game/Blade
 * Manages the neon slash trail for up to two hands.
 *
 * The trail is a fixed-length ring buffer of pointer positions.
 * Each frame the render layer reads trail state and draws a tapered, fading
 * line; colour shifts through CONFIG.slash.trailColors as the combo count grows.
 *
 * Functional API (contract): addTrailPoint, drawTrails, clearTrail, clearAllTrails.
 * Legacy class API (BladeTrail) is kept for PoC main.ts backwards-compat.
 */

import { CONFIG } from '../game-config.js';
import type { Vec2 } from '../types.js';

/** A single point in the trail history. */
export interface TrailPoint {
  x: number;
  y: number;
  /** Timestamp (ms) when this point was recorded — drives fade-out alpha. */
  timestamp: number;
}

/** Colour name bucket matching CONFIG.slash.trailColors keys. */
type TrailColorBucket = 'combo0' | 'combo1' | 'combo3' | 'combo5plus';

/**
 * Returns the trail colour for the given combo count from CONFIG.
 */
function trailColorForCombo(combo: number): string {
  let bucket: TrailColorBucket;
  if (combo >= 5) bucket = 'combo5plus';
  else if (combo >= 3) bucket = 'combo3';
  else if (combo >= 1) bucket = 'combo1';
  else bucket = 'combo0';
  return CONFIG.slash.trailColors[bucket];
}

/**
 * Accumulates pointer positions into a fixed-length trail buffer.
 * The render layer reads {@link BladeTrail.points} and {@link BladeTrail.color}
 * each frame to draw the blade effect.
 *
 * One instance per tracked hand.
 */
export class BladeTrail {
  private readonly _maxLength: number;
  private readonly _points: TrailPoint[] = [];
  private _combo = 0;

  constructor(maxLength = CONFIG.slash.trailLength) {
    this._maxLength = maxLength;
  }

  /**
   * Appends the latest pointer position to the trail.
   *
   * @param x - Pointer X in canvas pixels.
   * @param y - Pointer Y in canvas pixels.
   * @param timestamp - Current frame timestamp (ms).
   */
  push(x: number, y: number, timestamp: number): void {
    this._points.push({ x, y, timestamp });
    if (this._points.length > this._maxLength) {
      this._points.shift();
    }
  }

  /**
   * Updates the combo count so the trail colour reflects the current chain.
   * Call this whenever {@link ScoreManager.state.combo} changes.
   */
  setCombo(combo: number): void {
    this._combo = combo;
  }

  /**
   * Current trail colour derived from combo count.
   * Render layer uses this as the stroke colour for the newest trail segment.
   */
  get color(): string {
    return trailColorForCombo(this._combo);
  }

  /**
   * Trail points ordered oldest → newest.
   * Alpha for each segment = `life` where life = (point.timestamp - now) / CONFIG.slash.trailFadeMs
   * mapped to [0, 1]. Render layer computes this per-point.
   */
  get points(): readonly TrailPoint[] {
    return this._points;
  }

  /** Maximum line width at the newest point (tapers to 1 at oldest). */
  get maxWidth(): number {
    return CONFIG.slash.trailWidthMax;
  }

  /** Fade duration (ms) — passed to the render layer for alpha computation. */
  get fadeMs(): number {
    return CONFIG.slash.trailFadeMs;
  }

  /**
   * Prunes trail points older than CONFIG.slash.trailFadeMs from `nowMs`.
   * Call once per render frame to keep the buffer clean.
   */
  prune(nowMs: number): void {
    const cutoff = nowMs - CONFIG.slash.trailFadeMs;
    while (this._points.length > 0 && (this._points[0]?.timestamp ?? 0) < cutoff) {
      this._points.shift();
    }
  }

  /** Clears the trail (e.g. when the hand disappears). */
  clear(): void {
    this._points.length = 0;
  }
}

// ---------------------------------------------------------------------------
// Functional module API (per module contract)
// ---------------------------------------------------------------------------

/** Module-level trail state — one BladeTrail per hand. */
const _trails: [BladeTrail, BladeTrail] = [
  new BladeTrail(),
  new BladeTrail(),
];

/**
 * Append a new point to the blade trail for the given hand index.
 * Called once per render frame with the interpolated pointer position.
 * comboCount drives trail color per spec §4.1.
 */
export function addTrailPoint(handIndex: 0 | 1, pos: Vec2, comboCount: number): void {
  const trail = _trails[handIndex];
  trail.setCombo(comboCount);
  trail.push(pos.x, pos.y, performance.now());
}

/**
 * Draw all active trails onto the given PIXI.Graphics instance.
 * Clears graphics first. Call once per render frame.
 *
 * Each trail segment is drawn with alpha that fades from 1 (newest) to 0
 * (oldest) and line width that tapers from trailWidthMax to 1.
 */
export function drawTrails(graphics: { clear(): void; moveTo(x: number, y: number): void; lineTo(x: number, y: number): void; stroke(opts: { color: string; alpha: number; width: number }): void }): void {
  graphics.clear();

  const nowMs = performance.now();

  for (const trail of _trails) {
    trail.prune(nowMs);
    const pts = trail.points;
    if (pts.length < 2) continue;

    const color = trail.color;
    const fadeMs = trail.fadeMs;
    const maxWidth = trail.maxWidth;
    const count = pts.length;

    // Draw each segment independently so we can vary alpha + width per segment.
    for (let i = 1; i < count; i++) {
      const prev = pts[i - 1];
      const curr = pts[i];
      if (prev === undefined || curr === undefined) continue;

      // Normalise i to [0,1] — 0 = oldest, 1 = newest.
      const ageRatio = i / (count - 1);
      const timeAlpha = Math.max(0, (curr.timestamp - (nowMs - fadeMs)) / fadeMs);
      const alpha = ageRatio * timeAlpha;
      const width = 1 + (maxWidth - 1) * ageRatio;

      graphics.moveTo(prev.x, prev.y);
      graphics.lineTo(curr.x, curr.y);
      graphics.stroke({ color, alpha, width });
    }
  }
}

/** Clear trail state for one hand (e.g., when hand disappears). */
export function clearTrail(handIndex: 0 | 1): void {
  _trails[handIndex].clear();
}

/** Clear all trail state. */
export function clearAllTrails(): void {
  _trails[0].clear();
  _trails[1].clear();
}
